// S3 accounts — master admin screen.
//
// A global registry of EXTERNAL users (people/systems outside this project),
// modeled like the per-tenant basic-auth users: name / password / note. These
// accounts get granted read/write on individual tenants' S3 connections from
// the per-tenant S3 card (the s3-account-grants mechanism), where they show up
// as a distinct "External accounts" group alongside the tenant's basic-auth
// users.

import { useEffect, useRef, useState } from 'react';
import { I } from '../components/icons';
import { Modal, useToast, Empty } from '../components/ui';
import {
  fetchS3Accounts, createS3Account, updateS3Account, deleteS3Account,
  fetchS3AccountAccess, ApiError, type S3Account, type S3AccountAccess,
} from '../api/client';

// PasswordCell shows whether the account has a password set.
function PasswordCell({ password }: { password: string }) {
  if (password) {
    return (
      <span className="row" style={{ gap: 6, color: 'var(--green)', fontSize: 12.5, fontWeight: 500 }}>
        <I.CheckCirc size={14}/> Set
      </span>
    );
  }
  return <span className="muted" style={{ fontSize: 12.5 }}>Not set</span>;
}

export function S3AccountsScreen() {
  const toast = useToast();
  const [accounts, setAccounts] = useState<S3Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [editFor, setEditFor] = useState<S3Account | null>(null);
  const [accessFor, setAccessFor] = useState<S3Account | null>(null);
  const [deleteFor, setDeleteFor] = useState<S3Account | null>(null);
  const [deleting, setDeleting] = useState(false);

  const reload = async () => {
    setLoading(true);
    setErr(null);
    try {
      setAccounts(await fetchS3Accounts());
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = accounts.filter((a) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return a.name.toLowerCase().includes(needle)
      || (a.note || '').toLowerCase().includes(needle);
  });

  const remove = async () => {
    if (!deleteFor) return;
    setDeleting(true);
    try {
      await deleteS3Account(deleteFor.id);
      setDeleteFor(null);
      await reload();
      toast?.success('Account deleted', deleteFor.name);
    } catch (e) {
      toast?.error('Delete failed', e instanceof ApiError ? e.message : String(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page-narrow">
      <div className="page-head spread">
        <div>
          <h1 className="page-title">Global S3 accounts</h1>
          <p className="page-sub">
            External users — people or systems outside this project — with a name, password and note (like basic-auth users).
            Assign them read/write on a client's S3 connections from that client's <span className="mono">S3</span> tab.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setAddOpen(true)}>
          <I.Plus size={13}/> New account
        </button>
      </div>

      <div className="metric-grid" style={{ marginBottom: 14 }}>
        <div className="metric">
          <div className="metric-label">Accounts</div>
          <div className="metric-value">{accounts.length}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="row" style={{ gap: 10 }}>
            <div style={{ position: 'relative', width: 280 }}>
              <I.Search size={13} style={{ position: 'absolute', left: 9, top: 7, color: 'var(--text-3)' }}/>
              <input className="input" placeholder="Search name, note…"
                     style={{ paddingLeft: 30, height: 28, fontSize: 12 }}
                     value={q} onChange={(e) => setQ(e.target.value)}/>
            </div>
          </div>
          <span className="muted" style={{ fontSize: 12 }}>
            {loading ? 'Loading…' : `${filtered.length} shown`}
          </span>
        </div>

        {err && <div className="field-error" style={{ padding: 12 }}>Failed to load: {err}</div>}

        {!loading && !err && filtered.length === 0 && (
          <Empty title="No accounts yet"
                 message="Add an external user with the button above, then assign them to a client's S3 connections."/>
        )}

        {filtered.length > 0 && (
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 240 }}>Name</th>
                <th>Note</th>
                <th style={{ width: 130 }}>Password</th>
                <th style={{ width: 104 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id}>
                  <td>
                    <div className="row" style={{ gap: 8 }}>
                      <I.Users size={13} style={{ color: 'var(--text-3)' }}/>
                      <span style={{ fontWeight: 500 }}>{a.name}</span>
                    </div>
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{a.note || '—'}</td>
                  <td><PasswordCell password={a.password}/></td>
                  <td>
                    <div className="row" style={{ gap: 6 }}>
                      <button className="btn btn-ghost btn-icon btn-sm" title="Account access"
                              aria-label="Account access" onClick={() => setAccessFor(a)}>
                        <I.Layers/>
                      </button>
                      <button className="btn btn-ghost btn-icon btn-sm" title="Edit" aria-label="Edit account"
                              onClick={() => setEditFor(a)}>
                        <I.Settings/>
                      </button>
                      <button className="btn btn-ghost btn-icon btn-sm" title="Delete" aria-label="Delete account"
                              onClick={() => setDeleteFor(a)}>
                        <I.Trash/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <S3AccountModal open={addOpen} account={null}
                      onClose={() => setAddOpen(false)}
                      onSaved={async () => { setAddOpen(false); await reload(); toast?.success('Account created'); }}/>

      <S3AccountModal open={editFor !== null} account={editFor}
                      onClose={() => setEditFor(null)}
                      onSaved={async () => { setEditFor(null); await reload(); toast?.success('Account updated'); }}/>

      <S3AccountAccessModal account={accessFor} onClose={() => setAccessFor(null)}/>

      <Modal open={deleteFor !== null}
             onClose={() => { if (!deleting) setDeleteFor(null); }}
             title="Delete S3 account?"
             subtitle="Removes this external user. Any read/write grants they hold on clients' S3 connections are also cleared."
             footer={
               <>
                 <button className="btn" disabled={deleting} onClick={() => setDeleteFor(null)}>Cancel</button>
                 <button className="btn btn-danger-solid" disabled={deleting} onClick={remove}>
                   <I.Trash size={13}/> {deleting ? 'Deleting…' : 'Delete'}
                 </button>
               </>
             }>
        {deleteFor && (
          <div className="mono" style={{ fontSize: 12, padding: 10, background: 'var(--bg-2)', borderRadius: 6 }}>
            {deleteFor.name}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── Create / edit modal ─────────────────────────────────────────────────────

function S3AccountModal({
  open, account, onClose, onSaved,
}: {
  open: boolean;
  account: S3Account | null; // null = create
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const toast = useToast();
  const editing = account !== null;

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Seed only on the closed→open transition — NOT on every re-render. `account`
  // is a stable state ref today (no poll here), but keying the seed on it would
  // wipe in-progress edits if a background refresh were ever added. The ref guard
  // makes reopening re-seed while a re-render does not.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setName(account?.name ?? '');
      setPassword(account?.password ?? '');
      setShowPassword(false);
      setNote(account?.note ?? '');
      setSubmitting(false);
    }
    wasOpen.current = open;
  }, [open, account]);

  const canSubmit = name.trim().length > 0 && password.trim().length > 0;

  const genPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    const buf = new Uint32Array(20);
    crypto.getRandomValues(buf);
    setPassword(Array.from(buf, (n) => chars[n % chars.length]).join(''));
    setShowPassword(true);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (editing && account) {
        await updateS3Account(account.id, {
          name: name.trim(), password: password.trim(), note: note.trim(),
        });
      } else {
        await createS3Account({
          name: name.trim(),
          password: password.trim() || undefined,
          note: note.trim() || undefined,
        });
      }
      await onSaved();
    } catch (e) {
      toast?.error(editing ? 'Update failed' : 'Create failed', e instanceof ApiError ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}
           title={editing ? 'Edit S3 account' : 'New S3 account'}
           subtitle="An external user, outside this project. Grant them access to clients' S3 connections from each client's S3 tab."
           footer={
             <>
               <button className="btn" onClick={onClose}>Cancel</button>
               <button className="btn btn-primary" disabled={submitting || !canSubmit} onClick={submit}>
                 {submitting ? 'Saving…' : (editing ? 'Save' : 'Create')}
               </button>
             </>
           }>
      <div className="col" style={{ gap: 12 }}>
        <div className="field">
          <label className="field-label">Name <span className="req-dot">*</span></label>
          <input className="input" placeholder="e.g. jane-acme"
                 value={name} onChange={(e) => setName(e.target.value)} autoFocus/>
        </div>
        <div className="field">
          <label className="field-label">Password <span className="req-dot">*</span></label>
          <div className="row" style={{ gap: 8 }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
              <input className="input input-mono" style={{ paddingRight: 36, width: '100%' }}
                     type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                     value={password} onChange={(e) => setPassword(e.target.value)}/>
              <button type="button" className="btn btn-ghost btn-icon btn-sm"
                      style={{ position: 'absolute', right: 3, top: 3 }}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      title={showPassword ? 'Hide' : 'Show'}
                      onClick={() => setShowPassword((v) => !v)}>
                {showPassword ? <I.EyeOff size={14}/> : <I.Eye size={14}/>}
              </button>
            </div>
            <button type="button" className="btn" style={{ flexShrink: 0, gap: 5, height: 34 }}
                    onClick={genPassword}>
              <I.Refresh size={13}/> Generate
            </button>
          </div>
        </div>
        <div className="field">
          <label className="field-label">Note (optional)</label>
          <textarea className="input" rows={3} placeholder="What this account is for, who owns it, etc."
                    style={{ resize: 'vertical', minHeight: 64 }}
                    value={note} onChange={(e) => setNote(e.target.value)}/>
        </div>
      </div>
    </Modal>
  );
}

// ─── Reverse-view modal: where this account has access ───────────────────────

function S3AccountAccessModal({ account, onClose }: { account: S3Account | null; onClose: () => void }) {
  const [rows, setRows] = useState<S3AccountAccess[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!account) { setRows(null); setErr(null); return; }
    let cancelled = false;
    setRows(null); setErr(null);
    fetchS3AccountAccess(account.id)
      .then((r) => { if (!cancelled) setRows(r); })
      .catch((e) => { if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [account]);

  return (
    <Modal open={account !== null} onClose={onClose} size="md"
           title={`Access · ${account?.name ?? ''}`}
           subtitle="Every client S3 connection this account can use. Manage grants from each client's S3 tab."
           footer={
             <div className="spread" style={{ width: '100%', alignItems: 'center' }}>
               <span className="muted" style={{ fontSize: 12 }}>
                 {rows && rows.length > 0 ? `${rows.length} connection${rows.length === 1 ? '' : 's'}` : ''}
               </span>
               <button className="btn btn-primary btn-sm" onClick={onClose}>Close</button>
             </div>
           }>
      {err ? (
        <div className="field-error" style={{ padding: 10 }}>{err}</div>
      ) : rows === null ? (
        <div className="muted" style={{ fontSize: 12.5, padding: 10 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <Empty icon={<I.Layers size={26}/>} title="No access yet"
               message="This account isn't granted on any client's S3 connections. Add it from a client's S3 tab → Access."/>
      ) : (
        <div className="col" style={{ gap: 8 }}>
          {rows.map((r) => (
            <div key={r.clientId + ':' + r.connectionId} className="card card-pad"
                 style={{ background: 'var(--bg-1)', display: 'flex', alignItems: 'center',
                          justifyContent: 'space-between', gap: 16 }}>
              <div style={{ minWidth: 0 }}>
                <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 13.5 }}>{r.connectionName}</span>
                  {r.bucket && <span className="mono muted" style={{ fontSize: 12 }}>{r.bucket}</span>}
                </div>
                <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>
                  client <span className="mono" style={{ color: 'var(--text-2)' }}>{r.clientId}</span>
                </div>
              </div>
              <span className="badge" style={{ flexShrink: 0, whiteSpace: 'nowrap',
                background: r.canWrite ? 'var(--accent-soft)' : 'var(--bg-3)',
                color: r.canWrite ? 'var(--accent)' : 'var(--text-2)' }}>
                {r.canWrite ? 'read + write' : 'read only'}
              </span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
